import React, { useEffect, useState } from 'react';
import {
  MdMoreHoriz,
  MdOutlineKeyboardArrowLeft,
  MdOutlineKeyboardArrowRight,
} from 'react-icons/md';
import styled from 'styled-components';
import { Green4 } from '../../../app/theme/tokens/colors';
import { Select } from '../Inputs';

const PageButtons = styled.button`
  padding: 10px;
  margin: 2px;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 600;
  background-color: white;
  border-radius: 10px;
  color: ${Green4};
  border: 1px solid ${Green4};
`;

interface BasicPaginationChangeEvent {
  pageSize: string;
  page: string;
}

interface BasicPaginationProps {
  qtyItems: number;
  onChange?: (event: BasicPaginationChangeEvent) => void;
  style?: React.CSSProperties;
  page?: number;
  pageSize?: number;
  pageSizeOptions?: number[];
}

const MAX_PAGE_SIZE = 50;

export const BasicPagination: React.FC<BasicPaginationProps> = ({
  style,
  qtyItems,
  onChange,
  page,
  pageSize: pageSizeProp,
  pageSizeOptions,
}) => {
  const requestedPageSizes =
    Array.isArray(pageSizeOptions) && pageSizeOptions.length > 0
      ? pageSizeOptions
      : [10, 20, 50];
  const allowedPageSizes = requestedPageSizes.filter(
    (size) => Number.isFinite(size) && size > 0 && size <= MAX_PAGE_SIZE
  );
  const effectivePageSizes =
    allowedPageSizes.length > 0 ? allowedPageSizes : [10, 20, 50];
  const normalizePageSize = (value?: number) => {
    const cappedValue = Math.min(MAX_PAGE_SIZE, value ?? effectivePageSizes[0]);
    return effectivePageSizes.includes(cappedValue)
      ? cappedValue
      : effectivePageSizes[0];
  };

  const [pageSize, setPageSize] = useState<number>(() =>
    normalizePageSize(pageSizeProp)
  );
  const [currentPage, setCurrentPage] = useState<number>(page ?? 0);

  useEffect(() => {
    if (typeof pageSizeProp === 'number' && Number.isFinite(pageSizeProp)) {
      setPageSize(normalizePageSize(pageSizeProp));
    }
  }, [pageSizeProp]);

  useEffect(() => {
    if (typeof page === 'number' && Number.isFinite(page)) {
      setCurrentPage(page);
    }
  }, [page]);

  const paginate = (_pageSize: string) => {
    const parsedSize = parseInt(_pageSize, 10);
    setCurrentPage(0);
    setPageSize(parsedSize);

    if (typeof onChange === 'function') {
      onChange({
        pageSize: _pageSize,
        page: '0',
      });
    }
  };

  const totalPages = qtyItems > 0 ? Math.ceil(qtyItems / pageSize) : 0;

  const navigatePage = (page: number) => {
    if (page < 0) return;
    if (page > totalPages - 1) return;

    setCurrentPage(page);

    if (typeof onChange === 'function') {
      onChange({
        pageSize: pageSize.toString(),
        page: page.toString(),
      });
    }
  };

  const pagesOverLimit = totalPages > 6;
  const lastIdx = totalPages - 1;
  const last = lastIdx;
  const first = 0;

  // Janela de páginas visíveis (máx 6), sem alocar todas as páginas.
  // Mantém comportamento: tenta mostrar páginas ao redor da currentPage.
  let end = lastIdx;
  if (pagesOverLimit && currentPage + 4 < lastIdx) {
    end = currentPage + 2;
  }
  let start = end - 5;
  if (start < 0) start = 0;
  if (end < 0) end = 0;

  const visiblePages: number[] = [];
  for (let p = start; p <= end; p++) {
    visiblePages.push(p);
  }

  const lastIncluded = end >= last;
  const firstIncluded = start <= first;

  return (
    <div className="pagination" style={{ ...style }}>
      <div className="pagination-content">
        <div className="selection">
          <span>Itens por página</span>
          <Select
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              paginate(e.target.value)
            }
            value={pageSize.toString()}
          >
            {effectivePageSizes.map((size) => (
              <option key={size} value={size.toString()}>
                {size}
              </option>
            ))}
          </Select>
        </div>

        <div className="numbers">
          {/* seta esquerda */}
          {totalPages > 0 && currentPage > 0 ? (
            <MdOutlineKeyboardArrowLeft
              style={{ fontSize: '40px', color: Green4, cursor: 'pointer' }}
              onClick={() => navigatePage(currentPage - 1)}
            />
          ) : (
            <MdOutlineKeyboardArrowLeft
              style={{ visibility: 'hidden', fontSize: '40px' }}
            />
          )}

          {/* primeira página + "..." se tiver muita página */}
          {pagesOverLimit && !firstIncluded && (
            <>
              {currentPage === 0 ? (
                <PageButtons
                  onClick={() => navigatePage(first)}
                  style={{ color: 'white', backgroundColor: Green4 }}
                >
                  {1}
                </PageButtons>
              ) : (
                <PageButtons
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigatePage(first)}
                >
                  {1}
                </PageButtons>
              )}
              <MdMoreHoriz />
            </>
          )}

          {/* páginas intermediárias */}
          {visiblePages.map((p, idx) =>
            p === currentPage ? (
              <PageButtons
                onClick={() => navigatePage(p)}
                style={{ color: 'white', backgroundColor: Green4 }}
                key={idx}
              >
                {p + 1}
              </PageButtons>
            ) : (
              <PageButtons
                style={{ cursor: 'pointer' }}
                onClick={() => navigatePage(p)}
                key={idx}
              >
                {p + 1}
              </PageButtons>
            )
          )}

          {/* "..." + última página */}
          {pagesOverLimit && !lastIncluded && (
            <>
              <MdMoreHoriz />
              {currentPage === lastIdx ? (
                <PageButtons
                  onClick={() => navigatePage(last)}
                  style={{ color: 'white', backgroundColor: Green4 }}
                  key={lastIdx}
                >
                  {last + 1}
                </PageButtons>
              ) : (
                <PageButtons
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigatePage(last)}
                  key={lastIdx}
                >
                  {last + 1}
                </PageButtons>
              )}
            </>
          )}

          {/* seta direita */}
          {currentPage < lastIdx && totalPages > 0 && (
            <MdOutlineKeyboardArrowRight
              style={{ fontSize: '40px', color: Green4, cursor: 'pointer' }}
              onClick={() => navigatePage(currentPage + 1)}
            />
          )}
        </div>
      </div>
    </div>
  );
};
